import { type LocaleDefinition } from '@solinkcorp/locale';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import LocaleUtil from 'utils/LocaleUtil';
import { type CamerasDictionaryCamera } from 'utils/types';
import {
  REPEAT_ANNUALLY,
  REPEAT_DAILY,
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
  REPEAT_MONTHLY,
  REPEAT_WEEKLY,
} from 'vd-constants';

export function getLocationCount(
  spotCheck: ScheduledActivityEntity,
  camerasById: Record<string, CamerasDictionaryCamera>,
) {
  const locationIds = Array.from(
    new Set(spotCheck.cameraIds.map((cameraId: string) => camerasById[cameraId]?.location?.id)),
  );

  return locationIds.length;
}

export function getExpirationText(
  endsAt: number,
  reoccurringCount: number,
  locale: LocaleDefinition,
) {
  if (reoccurringCount === 0) {
    return locale.general.deleted;
  } else if (reoccurringCount === -1 && endsAt === -1) {
    return locale.usersPage.never;
  } else if (endsAt > 0) {
    return new Date(endsAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.numberOfOccurrences,
    replacements: { count: reoccurringCount },
  });
}

export type RepeatOptionValue =
  | typeof REPEAT_EVERY_15MIN
  | typeof REPEAT_EVERY_30MIN
  | typeof REPEAT_HOURLY
  | typeof REPEAT_EVERY_4H
  | typeof REPEAT_EVERY_6H
  | typeof REPEAT_DAILY
  | typeof REPEAT_WEEKLY
  | typeof REPEAT_MONTHLY
  | typeof REPEAT_ANNUALLY;

export function getFrequencyText(repeatOptionValue: RepeatOptionValue, locale: LocaleDefinition) {
  const mapOfFrequencyText = {
    [REPEAT_EVERY_15MIN]: locale.scheduledAIChat.every15Minutes,
    [REPEAT_EVERY_30MIN]: locale.scheduledAIChat.every30Minutes,
    [REPEAT_HOURLY]: locale.scheduledAIChat.hourly,
    [REPEAT_EVERY_4H]: locale.scheduledAIChat.every4Hours,
    [REPEAT_EVERY_6H]: locale.scheduledAIChat.every6Hours,
    [REPEAT_DAILY]: locale.scheduledAIChat.daily,
    [REPEAT_WEEKLY]: locale.scheduledAIChat.weekly,
    [REPEAT_MONTHLY]: locale.scheduledAIChat.monthly,
    [REPEAT_ANNUALLY]: locale.scheduledAIChat.annually,
  };

  return mapOfFrequencyText[repeatOptionValue] ?? 'One-time';
}
